import { type ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { lireProjet } from '../projets.js';
import { EchecApi } from '../api.js';
import { NavLink, Outlet, useOutletContext, useParams } from 'react-router';
import { CHEMINS } from '../routes.js';

interface Espace { readonly code: string; readonly nom: string; readonly role: string }

/**
 * LA PAGE D UN PROJET, ET SA NAVIGATION [contrat 28-BQ].
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ DEUX NIVEAUX, DEUX BARRES. Ce n est pas une duplication.              ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * La barre de Coquille porte les sections de l ESPACE -- equipe, cles,
 * validations -- qui appartiennent au client. Celle-ci porte les sections
 * du PROJET. Les fondre en une seule obligerait Equipe et Cles a recevoir
 * un projet dont elles n ont que faire : une donnee de complaisance, et le
 * type mentirait.
 *
 * MINIMALE, ET C EST DELIBERE. Une seule rubrique est prete ; les autres
 * viendront avec 29-F et 29-G. Un lien grise dit qu une rubrique existera,
 * son absence laisserait croire qu elle n est pas prevue.
 */
export function Projet(): ReactElement {
  const espace = useOutletContext<Espace>();
  const { projet } = useParams<{ projet: string }>();
  const code = projet ?? '';
  const [nom, poserNom] = useState<string | undefined>(undefined);
  const [echec, poserEchec] = useState<string | undefined>(undefined);
  useEffect(() => {
    let vivant = true;
    poserNom(undefined); poserEchec(undefined);
    void (async () => {
      try {
        const r = await lireProjet(espace.code, code);
        if (vivant) poserNom(r.projet.nom);
      } catch (e) {
        if (vivant) poserEchec(e instanceof EchecApi
          ? e.detail.code : 'Nom du projet indisponible.');
      }
    })();
    return () => { vivant = false; };
  }, [espace.code, code]);

  return (
    <>
      {/* LE NOM LISIBLE -- dette 60 LEVEE en 29-F.
          28-CH disait que cette coquille ne charge RIEN. Ce n est plus
          vrai, et pour une raison mesuree : `GET /projets/:projet` a
          desormais DEUX consommateurs reels -- ce titre et l ecran
          Parametres. La route n a pas ete creee pour embellir un titre
          [28-BW] ; le titre profite d une route qui devait naitre.
          LE CODE RESTE AFFICHE PENDANT LE CHARGEMENT : un titre vide
          puis rempli ferait sauter la mise en page, et le code est
          toujours vrai. En ECHEC il reste, DOUBLE d un message : la
          coquille porte les rubriques, la masquer couperait la
          navigation pour un titre manquant.
          NE PAS DERIVER UN NOM DEPUIS LE CODE : seconde verite,
          plausible et fausse. */}
      <p className="mono">{nom ?? code}</p>
      {echec !== undefined
        && <p className="message message--refus">{echec}</p>}
      <nav className="barre-rubriques" aria-label="Sections du projet">
        <NavLink to={CHEMINS.automatisations(espace.code, code)}
                 className={({ isActive }) =>
                   'barre-rubriques__lien' + (isActive ? ' barre-rubriques__lien--actif' : '')}>
          Automatisations
        </NavLink>
        <NavLink to={CHEMINS.environnements(espace.code, code)}
                 className={({ isActive }) =>
                   'barre-rubriques__lien' + (isActive ? ' barre-rubriques__lien--actif' : '')}>
          Environnements
        </NavLink>
        {/* DEUX RUBRIQUES OUVERTES EN 29-F. Chacune a une route moteur
            DEDIEE : la vue situee des executions du projet, et le PATCH
            des metadonnees. Aucune ne se contentait d un filtre client. */}
        <NavLink to={CHEMINS.executionsProjet(espace.code, code)}
                 className={({ isActive }) =>
                   'barre-rubriques__lien' + (isActive ? ' barre-rubriques__lien--actif' : '')}>
          Exécutions
        </NavLink>
        <NavLink to={CHEMINS.parametres(espace.code, code)}
                 className={({ isActive }) =>
                   'barre-rubriques__lien' + (isActive ? ' barre-rubriques__lien--actif' : '')}>
          Paramètres
        </NavLink>
      </nav>
      <Outlet context={espace} />
    </>
  );
}
